import type { CardColor, Card as CardInterface } from './card';
import type { GameState, GamePhase, DrawPenaltyType } from './game';
import type { Team } from './player';

/**
 * ネットワークメッセージタイプ
 */
export enum MessageType {
  // ルーム管理
  CREATE_ROOM = 'create_room',
  JOIN_ROOM = 'join_room',
  LEAVE_ROOM = 'leave_room',
  ROOM_CREATED = 'room_created',
  JOINED_ROOM = 'joined_room',
  PLAYER_JOINED = 'player_joined',
  PLAYER_LEFT = 'player_left',

  // ゲーム制御
  GAME_START = 'game_start',
  GAME_END = 'game_end',
  GAME_STATE_UPDATE = 'game_state_update',

  // ゲームアクション
  PLAY_CARD = 'play_card',
  DRAW_CARD = 'draw_card',
  DECLARE_UNO = 'declare_uno',
  UNO_DECLARED = 'uno_declared',

  // マッチ制御
  ROUND_END = 'round_end',
  START_NEXT_ROUND = 'start_next_round',

  // 接続管理
  PLAYER_DISCONNECTED = 'player_disconnected',
  PLAYER_RECONNECTED = 'player_reconnected',
  PLAYER_REPLACED_BY_AI = 'player_replaced_by_ai',
  RECONNECT_TO_ROOM = 'reconnect_to_room',

  // エラー
  ERROR = 'error'
}

/**
 * ネットワークメッセージ基本インターフェース
 */
export interface NetworkMessage<T = any> {
  /** メッセージタイプ */
  type: MessageType;
  /** ペイロード */
  payload: T;
  /** タイムスタンプ */
  timestamp: number;
}

/**
 * ルーム作成メッセージ
 */
export interface CreateRoomMessage {
  playerName: string;
}

/**
 * ルーム参加メッセージ
 */
export interface JoinRoomMessage {
  roomId: string;
  playerName: string;
}

/**
 * ルーム作成完了メッセージ
 */
export interface RoomCreatedMessage {
  roomId: string;
}

/**
 * ルーム参加完了メッセージ
 */
export interface JoinedRoomMessage {
  roomId: string;
  playerName: string;
}

/**
 * プレイヤー参加通知メッセージ
 */
export interface PlayerJoinedMessage {
  playerName: string;
  playerCount: number;
}

/**
 * プレイヤー退出通知メッセージ
 */
export interface PlayerLeftMessage {
  socketId: string;
}

/**
 * 公開ゲーム状態（サーバーから受信）
 */
export interface PublicGameState {
  phase: GamePhase;
  currentPlayerIndex: number;
  currentPlayerId: string;
  direction: 1 | -1;
  deckCount: number;
  topCard: CardInterface;
  drawCount: number;
  drawPenaltyType: DrawPenaltyType;
  winner: Team | null;
  declaredColor?: CardColor;
}

/**
 * ゲーム状態更新メッセージ（サーバーから受信）
 */
export interface GameStateUpdatePayload {
  gameState: PublicGameState;
  myHand: CardInterface[];
  teammateHands: CardInterface[][];
  opponentHandCounts: Record<string, number>;
  playerNames: Record<string, string>;
  playerTeams: Record<string, Team>;
  lifePoints: Record<string, number>;
  roundNumber: number;
  unoDeclarations: Record<string, boolean>;
}

/**
 * ゲーム状態更新メッセージ（従来互換）
 */
export interface GameStateUpdateMessage {
  gameState: Partial<GameState>;
}

/**
 * ラウンド終了メッセージ
 */
export interface RoundEndPayload {
  roundNumber: number;
  winner: Team;
  damageDealt: Record<string, number>;
  remainingCards: Record<string, number>;
  lifePoints: Record<string, number>;
}

/**
 * ゲーム終了メッセージ
 */
export interface GameEndMessage {
  winner: Team;
}

/**
 * カードプレイメッセージ
 */
export interface PlayCardMessage {
  cardId: string;
  declaredColor?: CardColor;
}

/**
 * UNO宣言メッセージ（サーバーから受信）
 */
export interface UnoDeclaredPayload {
  playerId: string;
  playerName: string;
}

/**
 * プレイヤー切断通知
 */
export interface PlayerDisconnectedPayload {
  playerId: string;
  reconnectTimeout: number;
}

/**
 * プレイヤーAI置換通知
 */
export interface PlayerReplacedByAIPayload {
  playerId: string;
  playerName: string;
}

/**
 * エラーメッセージ
 */
export interface ErrorMessage {
  message: string;
}
